import React, { useState, useEffect } from 'react';
import { GlassButton } from '../ui/GlassButton';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface DiscountCode {
  code_id: string;
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  max_uses?: number;
  use_count: number;
  expires_at?: string;
  active: boolean;
}

interface NewDiscountForm {
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: string;
  max_uses: string;
  expires_at: string;
}

const EMPTY_FORM: NewDiscountForm = {
  code: '',
  discount_type: 'percent',
  discount_value: '',
  max_uses: '',
  expires_at: '',
};

const ProductDiscounts: React.FC = () => {
  const [discounts, setDiscounts] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<NewDiscountForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products/discounts');
      setDiscounts(res.data);
    } catch (err: any) {
      setError('Failed to load discount codes.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscount = async () => {
    if (!form.code.trim()) {
      setFormError('Code is required.');
      return;
    }
    if (!form.discount_value || isNaN(Number(form.discount_value)) || Number(form.discount_value) <= 0) {
      setFormError('Enter a valid discount value.');
      return;
    }
    setCreating(true);
    setFormError('');
    try {
      const payload: Record<string, any> = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
      };
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses, 10);
      if (form.expires_at) payload.expires_at = form.expires_at;

      const res = await api.post('/products/discounts', payload);
      setDiscounts(prev => [res.data, ...prev]);
      setShowModal(false);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      setFormError(getApiError(err, 'Failed to create discount code.'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (discount: DiscountCode) => {
    if (!window.confirm(`Deactivate discount code "${discount.code}"?`)) return;
    setDeletingId(discount.code_id);
    try {
      await api.delete(`/products/discounts/${discount.code_id}`);
      setDiscounts(prev => prev.filter(d => d.code_id !== discount.code_id));
    } catch (err: any) {
      setError(getApiError(err, 'Failed to remove discount code.'));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  if (loading) return <div className="products-loading">Loading discount codes...</div>;

  return (
    <div className="discounts-container">
      <div className="discounts-header">
        <h2>Discount Codes</h2>
        <GlassButton
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() => { setForm(EMPTY_FORM); setFormError(''); setShowModal(true); }}
        >
          New Discount
        </GlassButton>
      </div>

      {error && <div className="products-error">{error}</div>}

      {discounts.length === 0 ? (
        <div className="products-empty-state">
          <p>No discount codes yet. Create one to offer promotions to your buyers.</p>
        </div>
      ) : (
        <div className="discounts-table-wrapper">
          <table className="discounts-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Value</th>
                <th>Uses</th>
                <th>Max Uses</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map(discount => (
                <tr key={discount.code_id}>
                  <td>
                    <code style={{ fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text)' }}>
                      {discount.code}
                    </code>
                  </td>
                  <td style={{ textTransform: 'capitalize', fontSize: '13px' }}>
                    {discount.discount_type === 'percent' ? 'Percent Off' : 'Fixed Amount'}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {discount.discount_type === 'percent'
                      ? `${discount.discount_value}%`
                      : `$${discount.discount_value.toFixed(2)}`}
                  </td>
                  <td>{discount.use_count}</td>
                  <td>
                    {discount.max_uses != null
                      ? discount.max_uses
                      : <span style={{ color: 'var(--text-muted)' }}>Unlimited</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {discount.expires_at
                      ? formatDate(discount.expires_at)
                      : <span style={{ color: 'var(--text-muted)' }}>Never</span>}
                  </td>
                  <td>
                    <span
                      className="discount-active-dot"
                      style={{ marginRight: '6px' }}
                      title={discount.active ? 'Active' : 'Inactive'}
                    >
                      <span
                        className={`discount-active-dot ${discount.active ? 'on' : 'off'}`}
                      />
                    </span>
                    <span style={{ fontSize: '13px', color: discount.active ? 'var(--success)' : 'var(--text-muted)' }}>
                      {discount.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <GlassButton
                      variant="danger"
                      size="sm"
                      icon={<Trash2 size={13} />}
                      onClick={() => handleDelete(discount)}
                      loading={deletingId === discount.code_id}
                      title="Deactivate and remove"
                    >
                      Remove
                    </GlassButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Discount Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => !creating && setShowModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h3>New Discount Code</h3>

            <div className="modal-form-group">
              <label>Code</label>
              <input
                type="text"
                placeholder="e.g. LAUNCH20"
                value={form.code}
                onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                maxLength={32}
              />
            </div>

            <div className="modal-form-group">
              <label>Discount Type</label>
              <select
                value={form.discount_type}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    discount_type: e.target.value as 'percent' | 'fixed',
                  }))
                }
              >
                <option value="percent">Percent Off</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div className="modal-form-group">
              <label>
                {form.discount_type === 'percent' ? 'Percent Off (%)' : 'Amount Off ($)'}
              </label>
              <input
                type="number"
                min="0.01"
                step={form.discount_type === 'percent' ? '1' : '0.01'}
                placeholder={form.discount_type === 'percent' ? 'e.g. 20' : 'e.g. 5.00'}
                value={form.discount_value}
                onChange={e => setForm(prev => ({ ...prev, discount_value: e.target.value }))}
              />
            </div>

            <div className="modal-form-group">
              <label>Max Uses (optional, leave blank for unlimited)</label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="e.g. 100"
                value={form.max_uses}
                onChange={e => setForm(prev => ({ ...prev, max_uses: e.target.value }))}
              />
            </div>

            <div className="modal-form-group">
              <label>Expiry Date (optional)</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={e => setForm(prev => ({ ...prev, expires_at: e.target.value }))}
              />
            </div>

            {formError && <div className="modal-error">{formError}</div>}

            <div className="modal-actions">
              <GlassButton variant="secondary" onClick={() => setShowModal(false)} disabled={creating}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                onClick={handleCreateDiscount}
                loading={creating}
                icon={<Plus size={14} />}
              >
                Create
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDiscounts;
